import type { SchemaChange } from "../../shared/api/client";
import type { CompiledManifest } from "../../shared/api/generated/contracts";
import { useT, type MessageKey } from "../../shared/i18n";
import { Card, GroupLabel } from "../../shared/ui";
import { ChangeTile } from "./ChangeTile";
import { tileKindOf } from "./tileKind";
import { groupChanges, type ChangeLine } from "./changes";

function extraOf(line: ChangeLine, t: (key: MessageKey) => string): string {
  return [
    ...(line.detailKeys ?? []).map((key) => t(key)),
    ...(line.detail === undefined ? [] : [line.detail]),
  ].join(", ");
}

const GROUP_TITLES: Record<"fields" | "views" | "print", MessageKey> = {
  fields: "draft.changes.group.fields",
  views: "draft.changes.group.views",
  print: "draft.changes.group.print",
};

/**
 * "What changes": the server change list (ADR 0201) against the version the
 * Draft was cut from. Owner-authored labels only, never record content.
 */
export function DraftChanges({
  changes,
  manifest,
  baseVersionNumber,
}: {
  changes: readonly SchemaChange[];
  manifest: CompiledManifest;
  /** `null` for a section that was never published. */
  baseVersionNumber: number | null | undefined;
}) {
  const t = useT();
  const isNew = baseVersionNumber === null || baseVersionNumber === undefined;
  const groups = groupChanges(changes, manifest);
  return (
    <section aria-label={t("draft.changes.title")}>
      <Card title={isNew ? t("draft.changes.new") : t("draft.changes.title")}>
        {isNew ? (
          <p className="text-body text-fg-muted">
            {t("draft.changes.newBody")}
          </p>
        ) : (
          <>
            <p className="-mt-2 mb-3 text-caption text-fg-muted">
              {t("draft.changes.relative")} {baseVersionNumber}
            </p>
            {groups.length === 0 ? (
              <p className="text-body text-fg-muted">
                {t("draft.changes.none")}
              </p>
            ) : (
              <div className="flex flex-col gap-4">
                {groups.map(({ group, lines }) => (
                  <section key={group} aria-label={t(GROUP_TITLES[group])}>
                    <GroupLabel>{t(GROUP_TITLES[group])}</GroupLabel>
                    <ul className="mt-1.5">
                      {lines.map((line) => (
                        <li
                          key={line.key}
                          className="flex items-center gap-3 border-b border-line-subtle py-2 text-body last:border-b-0"
                        >
                          <ChangeTile kind={tileKindOf(line.messageKey)} />
                          <span className="min-w-0 flex-1">
                            {t(line.messageKey)}
                            {line.subject && <> «{line.subject}»</>}
                            {extraOf(line, t) && (
                              <span className="text-fg-muted">
                                {" "}
                                — {extraOf(line, t)}
                              </span>
                            )}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </section>
                ))}
              </div>
            )}
          </>
        )}
      </Card>
    </section>
  );
}
